<template>
  <Combobox v-model="model" as="div" :multiple="multiple">
    <ComboboxLabel v-if="label" :for="id" class="form-label">{{ label }}</ComboboxLabel>
    <div class="position-relative">
      <span v-if="icon" class="input-group-text start-0">
        <Icon class="flex-shrink-0" :icon="`material-symbols:${icon}`" />
      </span>
      <ComboboxInput
        :id="id"
        autocomplete="off"
        :aria-describedby="`${id}.form-text ${id}.invalid-feedback`"
        :aria-controls="`${id}-listbox`"
        :popovertarget="`${id}-listbox`"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :class="[
          'form-control rounded',
          size ? `form-control-${size}` : undefined,
          {'is-invalid': error && serverError === true},
          {'is-valid': !error && serverError === true},
        ]"
        :style="[
          {'padding-left': icon ? '2.25rem' : '0.75rem'},
          {'padding-right': loading ? '2.25rem' : '0.75rem'},
        ]"
        :display-value="(v: any) => getDisplayValue(v)"
        @change="change"
        @keyup.enter="createOption"
        @focus="focus"
        @blur="blur"
      />
      <span v-if="loading" class="input-group-text spinner end-0">
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </span>
    </div>
    <div v-if="text" :id="`${id}.form-text`" class="form-text">{{ text }}</div>
    <div v-if="error" :id="`${id}.invalid-feedback`" class="invalid-feedback">{{ error }}</div>
    <ComboboxOptions
      :id="`${id}-listbox`"
      ref="popover"
      popover="manual"
      static
      :class="['dropdown-menu custom-scrollbar', size ? `dropdown-menu-${size}` : undefined]"
    >
      <ComboboxOption
        v-if="creatable && queryValue && !queryExists"
        :value="queryValue"
        as="template"
        @click="createOption"
      >
        <li class="dropdown-item d-flex align-items-center gap-2">
          "{{ query }}"
          <p
            class="d-flex flex-nowrap align-items-center text-secondary gap-1 m-0"
            style="font-size: 0.75rem"
          >
            <Icon class="flex-shrink-0 fs-6" icon="material-symbols:keyboard-return" />
            <span>Add</span>
          </p>
        </li>
      </ComboboxOption>
      <li v-if="filteredOptions.length === 0" class="dropdown-item disabled">'No results'</li>
      <ComboboxOption
        v-for="(o, i) in filteredOptions"
        v-slot="{selected, active}"
        :key="i"
        :value="o"
        as="template"
      >
        <li
          :class="[
            'dropdown-item text-truncate',
            {'headless-selected': selected, 'headless-active': active},
          ]"
        >
          <slot :option="o">{{ o[customKey] }}</slot>
        </li>
      </ComboboxOption>
    </ComboboxOptions>
  </Combobox>
</template>
<script lang="ts" setup>
import {
  Combobox,
  ComboboxLabel,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/vue';
import {
  type ComboBoxOption,
  type ComboBoxProps,
} from '@/components/shared/custom/ComboBox/ComboBox.types';

const {
  customKey = 'value',
  icon = undefined,
  label = undefined,
  size = undefined,
  placeholder = undefined,
  text = undefined,
  options,
  multiple = false,
  creatable = false,
  error = undefined,
} = defineProps<ComboBoxProps>();

const emits = defineEmits<{change: [query: string]}>();
const model = defineModel<ComboBoxOption | ComboBoxOption[] | null>({required: true});

const popover = useTemplateRef<InstanceType<typeof ComboboxOptions>>('popover');

const getDisplayValue = (value: ComboBoxOption | ComboBoxOption[] | null) => {
  if (Array.isArray(value)) return value.map((v) => v[customKey]).join(', ');
  else if (value) return value[customKey];
  else return undefined;
};

const customOptions = ref<ComboBoxOption[]>([]);
const allOptions = computed(() => [...customOptions.value, ...options]);

const query = ref('');
const queryValue = computed(() =>
  query.value === '' ? null : {id: null, [customKey]: query.value},
);

const filteredOptions = computed(() =>
  query.value === ''
    ? allOptions.value
    : allOptions.value.filter((o) =>
        o[customKey]
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, '')),
      ),
);

const queryExists = computed(
  () => allOptions.value.find((o) => o[customKey] === query.value) !== undefined,
);

const createOption = (event: Event) => {
  if (queryValue.value && creatable && !queryExists.value) {
    customOptions.value.push(queryValue.value);
    query.value = '';
    const el = event.target as HTMLInputElement;
    el.value = '';
  }
};

const change = (event: Event) => {
  const source = event.target as HTMLInputElement;
  query.value = source.value;
  emits('change', query.value);
};

const focus = (event: Event) => {
  const source = event.target as HTMLInputElement;
  // Select the current text so the first keystroke replaces it instead of inserting into it.
  source.select();
  const rect = source.getBoundingClientRect();
  if (popover.value) {
    const el = popover.value.$el as HTMLElement;
    // N.B. Be careful when changing this!
    el.style.maxHeight = `${window.innerHeight - rect.bottom - 56}px`;
    // Positioned explicitly instead of via CSS anchor positioning: `anchor()`/`showPopover({source})`
    // implicit anchoring isn't reliably enabled across Chromium-based browsers (e.g. Arc), where it
    // silently falls back to `auto` and drops the dropdown at the top-left of the viewport.
    el.style.top = `${rect.bottom}px`;
    el.style.left = `${rect.left}px`;
    el.style.width = `${rect.width}px`;
  }
  popover.value?.$el.showPopover({source});
};

const blur = (event: Event) => {
  const el = event.target as HTMLInputElement;
  if (model.value) el.value = getDisplayValue(model.value);
  popover.value?.$el.hidePopover();
  query.value = '';
};
</script>
<style lang="scss" scoped>
/* Overwrite the icon positions */
.input-group-text {
  position: absolute;
  top: 0;
  height: 100%;
  border: 0;
  background-color: transparent;
}

.spinner-border {
  --bs-spinner-border-width: 0.1rem;
}

/* Position is set explicitly via JS (see `focus` handler) rather than CSS anchor positioning. */
.dropdown-menu {
  margin-top: 0.25rem;
  inset: auto;
}

/* Using menu styles without the Bootstrap dropdown.
Need to overwrite display when the popover shown. */
.dropdown-menu:popover-open {
  display: block !important;
}

.dropdown-item {
  cursor: pointer;
}

.headless-selected {
  color: var(--bs-dropdown-link-active-color);
  text-decoration: none;
  background-color: var(--bs-dropdown-link-active-bg);
}

.headless-active {
  color: var(--bs-dropdown-link-hover-color);
  background-color: var(--bs-dropdown-link-hover-bg);
}
</style>
